import {Injectable} from '@angular/core';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {ICreatePoolPayload, IPoolDefect, IPoolSearchResult, IUpdatePoolPayload, PoolApiService} from './pool-api.service';
import {MmProjectLoaderService} from '../../../mm-project/mm-project-loader.service';
import {MmProjectDownloadService} from '../../../mm-project/mm-download.service';
import {Project} from '@docma-mm-core/project/project';
import {Pool} from '@docma-mm-core/pool/pool';
import {toPoolVisibility, toPoolVisibleForValue} from '../pool-left-pane/pool-left-pane.component';
import {filter, map} from 'rxjs/operators';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {MmFilterService} from '../../mm-filter.service';

@Injectable()

export class PoolEditDataService {

  private newPoolCreatedSubject = new BehaviorSubject<any>(null);
  public newPoolCreated$ = this.newPoolCreatedSubject.asObservable();

  private poolUpdatedSubject = new BehaviorSubject<any>(null);
  public poolUpdated$ = this.poolUpdatedSubject.asObservable();

  private poolDeletedSubject = new BehaviorSubject<any>(null);
  public poolDeleted$ = this.poolDeletedSubject.asObservable();

  constructor(private poolApiService: PoolApiService,
              private readonly projectLoaderService: MmProjectLoaderService,
              private readonly downloadService: MmProjectDownloadService,
              private readonly filterService: MmFilterService) {
  }

  async addPool(poolToAdd: ICreatePoolPayload, project: Project) {
    const newPool = await firstValueFrom(this.poolApiService.createProjectPool(project.Id, poolToAdd));
    const pools = await firstValueFrom(this.downloadService.downloadPools(project, true));
    const updatedPools = await this.projectLoaderService.loadPoolsByAddedOrUpdated(project, pools, true).toPromise();
    const completePoolData = pools.find(p => p.Id === newPool.Id) || newPool;
    this.newPoolCreatedSubject.next({Pool: completePoolData, Pools: pools});
    return completePoolData;
  }

  async updatePool(poolToUpdatePayload: IUpdatePoolPayload, project: Project, poolToUpdate: Pool) {
    await firstValueFrom(this.poolApiService.updateProjectPool(project.Id, poolToUpdate.Id, poolToUpdatePayload));
    const upd = {...poolToUpdate};
    upd.VisibleFor = poolToUpdatePayload.VisibleFor;

    const user = await firstValueFrom(this.projectLoaderService.projectUserPermissions
      .pipe(filter(userPerm => userPerm.loadState.status === LoadStatusEnum.Loaded)).pipe(map(x => x.data.User)));

    const userPoolVisibility = +toPoolVisibility(user.Company.ViewPoint);

    // tslint:disable-next-line:no-bitwise
    if ((toPoolVisibleForValue(upd.VisibleFor) & userPoolVisibility) === userPoolVisibility) {
      const pools = await this.projectLoaderService.loadPoolsByAddedOrUpdated(project, upd, true).toPromise();
      return {Pool: poolToUpdate, Pools: pools};
    } else {
      const poolsByRemove = await this.projectLoaderService.loadPoolsByDeleted(project, poolToUpdate.Id, true).toPromise();
      this.poolDeletedSubject.next({Pool: poolToUpdate, Pools: poolsByRemove});
      return {Pool: poolToUpdate, Pools: poolsByRemove};
    }
  }

  async deletePool(project: Project, poolForDelete: Pool) {
    await firstValueFrom(this.poolApiService.deleteProjectPool(project.Id, poolForDelete.Id));
    const pools = await this.projectLoaderService.loadPoolsByDeleted(project, poolForDelete.Id, true).toPromise();
    this.poolDeletedSubject.next({Pool: poolForDelete, Pools: pools});
    this.filterService.removePoolExclusionFilter(poolForDelete.Id);
    this.filterService.removePoolInclusionFilter(poolForDelete.Id);
    return poolForDelete;
  }

  async addDefectsToPool(projectId: number, poolId: number, defects: IPoolDefect[]) {
    const result = await firstValueFrom(this.poolApiService.addDefectsToPool(projectId, poolId, defects));
    return result;
  }

  async removeDefectsFromPool(projectId: number, poolId: number, defects: IPoolDefect[]) {
    const result = await firstValueFrom(this.poolApiService.removeDefectsFromPool(projectId, poolId, defects));
    return result;
  }

  async getPoolsWithUpdatedCounts(projectId: number, poolIds: number[]): Promise<IPoolSearchResult[]> {
    const result = await firstValueFrom(this.poolApiService.getPoolsWithUpdatedCounts(projectId, poolIds));
    return result.Data;
  }

}

